import {
	Box,
	CircularProgress,
	Grid2,
	IconButton,
	InputAdornment,
	Pagination,
	TextField,
	Typography
} from "@mui/material";
import React, { ChangeEvent, FC, FormEvent, useMemo, useState } from "react";
import TableSearchInput from "../../../components/TableSearchInput";
import "../index.css";
import PersonIcon from "@mui/icons-material/Person";
import moment from "moment";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import SessionDetailsDialog from "./SessionDetailsDialog";
import { useGetActiveSessionsQuery } from "../../../redux/reducers/userManagement.reducer";
import { IGetActiveSessionsRequestData } from "../../../types";
import { ROWS_PER_PAGE } from "../../../utils/constants";
import { Search } from "@mui/icons-material";

interface IActiveEnforcementDetails {
	id: number;
	officer_name: string;
	location_name: string;
	latitude: string;
	longitude: string;
	interceptor_id: string;
	start_time: string;
}

export interface IActiveSessionDetails {
	id: number;
	user_name: string;
	active_since: string;
	enforcement: IActiveEnforcementDetails | null;
}

const ActiveSessions: FC = () => {
	const [filters, setFilters] = useState<IGetActiveSessionsRequestData>({ page: 1 });
	const [sessionDetailsDialogOpen, setSessionDetailsDialogOpen] = useState<boolean>(false);
	const [selectedSession, setSelectedSession] = useState<IActiveSessionDetails | null>(null);
	const [pageNumberInput, setPageNumberInput] = useState<string>("");

	// APIS
	// GET ACTIVE SESSIONS
	const {
		data: getActiveSessionsResponse,
		isLoading: getActiveSessionsLoading,
		isFetching: getActiveSessionsFetching,
		refetch: refetchActiveSessions
	} = useGetActiveSessionsQuery(filters);

	const activeSessionsList = useMemo<IActiveSessionDetails[]>(() => {
		if (!getActiveSessionsResponse) return [];
		return getActiveSessionsResponse.results.map((item) => ({
			id: item.id,
			user_name: item.full_name,
			active_since: item.last_login,
			enforcement: null
		}));
	}, [getActiveSessionsResponse]);

	const pageCount = useMemo<number>(() => {
		const sessionsCount = getActiveSessionsResponse?.count ?? 0;
		return Math.ceil(sessionsCount / ROWS_PER_PAGE);
	}, [getActiveSessionsResponse?.count]);

	function handleChangePageNumberInput(event: ChangeEvent<HTMLInputElement>) {
		setPageNumberInput(event.target.value);
	}

	function handleSubmitSearchInputs(input: string) {
		setFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (input) updatedFilters.search = input;
			else delete updatedFilters.search;

			return updatedFilters;
		});
	}

	function handleOpenSessionDetailsDialog(sessionDetails: IActiveSessionDetails) {
		setSelectedSession(sessionDetails);
		setSessionDetailsDialogOpen(true);
	}

	function handleCloseSessionDetailsDialog() {
		setSessionDetailsDialogOpen(false);
		refetchActiveSessions();
	}

	function handlePageChange(page: number) {
		setFilters((currentFilters) => ({ ...currentFilters, page }));
	}

	function handleSkipToPage(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (pageNumberInput) {
			handlePageChange(Number(pageNumberInput));
			setPageNumberInput("");
		}
	}

	return (
		<Box className="page-content-wrapper">
			<Box className="page-header-wrapper">
				<TableSearchInput showClearButton={!!filters.search} onSubmit={handleSubmitSearchInputs} />
			</Box>
			<Box className="page-content">
				<Box sx={{ flex: 1, display: "flex", flexDirection: "column", marginBottom: "1rem" }}>
					{getActiveSessionsLoading || getActiveSessionsFetching ? (
						<Box
							sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", width: "100%" }}
						>
							<CircularProgress />
						</Box>
					) : (
						<Grid2 container spacing={2}>
							{activeSessionsList.map((sessionItem) => (
								<Grid2 size={{ xs: 12, md: 3 }} key={sessionItem.id}>
									<Box className="session-card-wrapper" onClick={() => handleOpenSessionDetailsDialog(sessionItem)}>
										<Box className="session-card-icon">
											<PersonIcon fontSize="large" />
										</Box>

										<Box className="session-card-details">
											<Box>
												<Typography fontWeight={500}>{sessionItem.user_name}</Typography>

												<Typography variant="body2" color="success">
													Active since {moment.duration(moment(sessionItem.active_since).diff(moment())).humanize()}
												</Typography>

												{sessionItem.enforcement ? (
													<Typography variant="body2" sx={{ color: "#FF9C45" }}>
														Enforcement Ongoing
													</Typography>
												) : null}
											</Box>

											<InfoOutlineIcon fontSize="small" color="info" />
										</Box>
									</Box>
								</Grid2>
							))}
						</Grid2>
					)}
				</Box>

				{/* Pagination */}
				{(getActiveSessionsResponse?.count ?? 0) > ROWS_PER_PAGE ? (
					<Box className="sessions-list-pagination-wrapper">
						<Pagination
							color="primary"
							count={pageCount}
							page={filters.page}
							onChange={(_event, newPage) => handlePageChange(newPage)}
						/>

						<Box sx={{ width: "200px" }} component="form" noValidate onSubmit={handleSkipToPage}>
							<TextField
								fullWidth
								placeholder="Page"
								size="small"
								autoComplete="off"
								value={pageNumberInput}
								onChange={handleChangePageNumberInput}
								slotProps={{
									input: {
										endAdornment: (
											<InputAdornment position="end">
												<IconButton edge="end" type="submit">
													<Search fontSize="small" />
												</IconButton>
											</InputAdornment>
										)
									}
								}}
							/>
						</Box>
					</Box>
				) : null}
			</Box>

			<SessionDetailsDialog
				open={sessionDetailsDialogOpen}
				sessionDetails={selectedSession}
				onClose={handleCloseSessionDetailsDialog}
			/>
		</Box>
	);
};

export default ActiveSessions;
